import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { toastr } from 'react-redux-toastr';
import { getPrinters } from '../../redux/actions/SettingsActions';
import { updateCurrentUserDefaultPrinter } from '../../redux/actions/UsersActions';
import { formatNumber } from '../../lib/utils';
import Modal from '../commonComponents/util/Modal';
import LoadingSpinner from '../commonComponents/util/LoadingSpinner';
import FadeOutErrorMessage from '../commonComponents/util/FadeOutErrorMessage';

class PrintModal extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedPrinterId: '',
            showPrinterSelect: false,
            setDefaultPrinter: false,
            initializing: true,
            submitting: false,
            printError: false,
            defaultPrinterError: false
        };

        this.close = this.close.bind(this);
        this.continue = this.continue.bind(this);
    }

    async componentDidMount() {
        const printers = await this.props.actions.getPrinters();
        this.initializePrinterSelection(printers && printers.data);
    }

    findPrinter(printerId, printers = this.props.printers.data) {
        if (printerId === null || printerId === undefined || printerId === '') return null;

        const normalizedPrinterId = String(printerId).trim();
        return (printers || []).find(printer =>
            String(printer.printerId).trim() === normalizedPrinterId) || null;
    }

    initializePrinterSelection(printers) {
        const defaultPrinter = this.findPrinter(
            this.props.userProfile.defaultPrinterId,
            printers || this.props.printers.data
        );

        this.setState({
            selectedPrinterId: defaultPrinter ? defaultPrinter.printerId : '',
            showPrinterSelect: !defaultPrinter,
            setDefaultPrinter: false,
            initializing: false
        });
    }

    close() {
        if (this.state.submitting || this.props.printing.loading) return;

        this.props.close();
    }

    async continue() {
        if (this.state.submitting) return;

        const selectedPrinter = this.findPrinter(this.state.selectedPrinterId);
        if (this.props.printers.loading || !selectedPrinter) return;

        this.setState({
            submitting: true,
            printError: false,
            defaultPrinterError: false
        });

        let defaultPrinterError = false;
        if (this.state.setDefaultPrinter &&
            String(this.props.userProfile.defaultPrinterId) !== String(selectedPrinter.printerId)) {
            const response = await this.props.actions.updateCurrentUserDefaultPrinter(selectedPrinter.printerId);
            defaultPrinterError = !response || !response.success;
            if (defaultPrinterError) this.setState({ defaultPrinterError: true });
        }

        await this.props.print(selectedPrinter.printerId);
        if (this.props.printing.success) {
            if (defaultPrinterError) {
                toastr.error(
                    'Default printer was not saved',
                    'The checks were sent to print, but your printer preference could not be saved.'
                );
            }
            this.props.close();
            this.props.onPrintSuccess();
        } else {
            this.setState({ submitting: false, printError: true });
        }
    }

    chooseDifferentPrinter() {
        this.setState({
            selectedPrinterId: '',
            showPrinterSelect: true,
            setDefaultPrinter: false,
            printError: false,
            defaultPrinterError: false
        });
    }

    selectPrinter(event) {
        this.setState({
            selectedPrinterId: event.target.value,
            printError: false,
            defaultPrinterError: false
        });
    }

    render() {
        const selectedPrinter = this.findPrinter(this.state.selectedPrinterId);
        const showPrinterSelect = this.state.showPrinterSelect || !selectedPrinter;
        const printersLoading = this.state.initializing || this.props.printers.loading;
        const submitting = this.state.submitting || this.props.printing.loading;

        return (
            <Modal
                modalShowing={true}>
                <div className="modal-container">
                    <div className="modal large-modal">
                        <div className="modal-header">
                            <h4 className="page-header">Print {formatNumber(this.props.count)} Check{this.props.count === 1 ? "" : "s"}</h4>
                            <i className={`icon ion-ios-close-empty${submitting ? ' disabled' : ''}`} onClick={this.close} />
                        </div>
                        <div className="print-modal-box">
                            <div className="print-modal-pic">
                                <img src="/images/Print.png" alt="print" />
                            </div>
                            {printersLoading ? <LoadingSpinner height="20" /> :
                                (!showPrinterSelect ? <div>
                                    <p>{selectedPrinter.name}</p>
                                    <button className="print-modal-btn" onClick={this.chooseDifferentPrinter.bind(this)}>
                                        Choose a different printer
                                    </button>
                                </div> :
                                <div>
                                    <div className="custom-select">
                                        <select disabled={!this.props.printers.data.length}
                                            value={this.state.selectedPrinterId}
                                            onChange={this.selectPrinter.bind(this)}>
                                            <option value="" hidden>Select Printer</option>
                                            {this.props.printers.data.map(printer =>
                                                <option key={printer.printerId} value={printer.printerId}>[{printer.computerName}] {printer.name}</option>)}
                                        </select>
                                        <i className="material-icons">arrow_drop_down</i>
                                    </div>
                                    <div className="custom-check-container">
                                        <input type="checkbox" id="default" className="custom-check"
                                            checked={this.state.setDefaultPrinter}
                                            onChange={() => this.setState({ setDefaultPrinter: !this.state.setDefaultPrinter })} />
                                        {selectedPrinter && <label htmlFor="default" className="secondary-text">Always use this printer</label>}
                                    </div>
                                </div>
                                )}
                        </div>

                        <FadeOutErrorMessage show={!this.props.printers.success} message={`Could not retrieve printers.  ${this.props.printers.message || "Please try again."}`} />

                        <div className="modal-btns text-right">
                            {submitting ? <LoadingSpinner height="20" alignRight={true} /> :
                                <div>
                                    <button className="btn btn-accent" onClick={this.close}>Cancel</button>
                                    <button className="btn btn-primary" onClick={this.continue}
                                        disabled={printersLoading || !selectedPrinter || submitting}>Continue</button>

                                    <FadeOutErrorMessage show={this.state.defaultPrinterError} message="The printer could not be saved as your default. You can still print with it." />
                                    <FadeOutErrorMessage show={this.state.printError} message="Something went wrong and the print job could not be completed.  Please try again." />
                                </div>}
                        </div>
                    </div>
                </div>
            </Modal>
        )
    }
}

function mapStateToProps(state, ownProps) {
    return {
        printers: state.settings.printers,
        printing: state.checkPrinting.printing,
        userProfile: state.userProfile.profile.data,
    }
}

function mapDispatchToProps(dispatch) {
    return {
        actions: bindActionCreators({ getPrinters, updateCurrentUserDefaultPrinter }, dispatch)
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(PrintModal);



// WEBPACK FOOTER //
// ./src/components/printingComponents/PrintModal.jsx